/**
 * Loads the customer-facing Collection list. Follows the useState/useEffect
 * `{data, loading, error, reload}` pattern used elsewhere in this SPA
 * (`usePublishEntitlement.ts`) — no React Query in this app (architecture §9).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCollections } from './api';
import type { Collection, CollectionListQuery, PaginatedCollections } from './types';

export interface UseCollections {
  data: Collection[] | null;
  meta: PaginatedCollections['meta'] | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useCollections(query: CollectionListQuery = {}): UseCollections {
  const [data, setData] = useState<Collection[] | null>(null);
  const [meta, setMeta] = useState<PaginatedCollections['meta'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { page, pageSize, occasionType, sort } = query;

  // Prev/Next aren't disabled while `loading` (matches this app's existing
  // loading-state conventions), so a fast double-click can have two fetches
  // in flight at once. Without this guard, an out-of-order response (e.g.
  // the page-2 request resolving after the page-3 one) would overwrite the
  // newer, still-in-flight page's eventual result with stale data.
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCollections({ page, pageSize, occasionType, sort });
      if (requestId !== requestIdRef.current) return; // a newer request has since started — ignore this stale response
      // Never re-sorted client-side — rendered in exactly the order the backend returns (architecture §4).
      setData(result.items);
      setMeta(result.meta);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load the catalog.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, occasionType, sort]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, meta, loading, error, reload };
}
